import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { TabStripComponent } from '@progress/kendo-angular-layout'

import {
  ContentRecord,
  DeployItem,
  GeneratedContentDetails,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools } from '@cyborg-monorepo/hunter/matchboi'
import { SnackbarService } from '@hyperkit'

import { CompanyService } from '../../services/company.service'
import { ContentService } from '../../services/content.service'

@Component({
  standalone: false,
  selector: 'deployment-queue',
  templateUrl: './deployment-queue.html',
  styleUrls: ['./deployment-queue.scss'],
})
export class DeploymentQueueComponent implements OnInit, AfterViewInit {
  private companyService = inject(CompanyService)
  private contentService = inject(ContentService)

  @ViewChild('tabstrip') public tabstrip: TabStripComponent
  @ViewChild('drawerContent') public drawerContent: ElementRef<HTMLElement>
  public drawerExpanded = false
  private lastFocusedElement: HTMLElement | null = null
  @Input() public deployItem: DeployItem
  @Output()
  public onMarkForDeployedEvent: EventEmitter<void> = new EventEmitter<void>()
  @Output()
  public onRemoveDeployedEvent: EventEmitter<void> = new EventEmitter<void>()
  private compatibleContentSet: Set<ContentRecord> = new Set()
  public compatibleContent: Array<ContentRecord>
  public contents: Array<ContentRecord>
  public contentDetails: ContentRecord
  public contentView: string
  private tool: SupportedTools = localStorage.getItem('tool') as SupportedTools
  private otherTools: Array<string> = [
    'CrowdStrike',
    'Snort',
    'CarbonBlack',
    'Microsoft Defender',
  ]
  private categories = JSON.parse(localStorage.getItem('categories'))
  public staged = false
  public deployed = true
  public notFullyMatched: boolean
  public generatedContentDetails: GeneratedContentDetails
  public outdated = false
  public latestHuntPackageChangeLog: {
    date: string
    note: string
  }
  public latestContentChangeLog: Array<{
    tool: string
    goal: string
    categories: string
    date: string
    note: string
  }>
  public environmentContent
  public readonly snack = inject(SnackbarService)

  public ngOnInit(): void {
    this.getCompatibleContent()
    this.getOutdated()
    this.environmentContent = this.contentService.getHuntPackageContent(
      this.deployItem.huntPackageUUID,
    )
  }

  public ngAfterViewInit(): void {
    this.tabstrip.selectTab(0)
  }

  public getOutdated(): void {
    if (
      this.deployItem.deployedAt &&
      new Date(this.deployItem.deployedAt) <
        new Date(this.deployItem.huntPackage.last_updated)
    ) {
      this.outdated = true
    }
  }

  public getCompatibleContent(): void {
    this.contents = this.deployItem.huntPackage.content.records
    this.contents
      .filter((contentRecord) => contentRecord.tool === this.tool)
      .some((contentRecord) => {
        contentRecord.deployment_requirements.categories.forEach((cat) => {
          if (this.categories.includes(cat)) {
            this.compatibleContentSet.add(contentRecord)
          }
        })
      })
    this.compatibleContent = Array.from(this.compatibleContentSet)

    // Adding due to not tracking Tools outside of mapping (Only SIEM)
    const otherContent = this.contents.filter((element) => {
      return this.otherTools.includes(element.tool)
    })
    this.compatibleContent.push(...otherContent)
  }

  public openContentDetails(content: ContentRecord): void {
    this.openDrawer()
    this.contentDetails = content
  }

  // Could not figure out how to inject this
  public getContent(content: ContentRecord): void {
    this.contentService.getContent(content).subscribe((generatedContent) => {
      this.generatedContentDetails = generatedContent
    })
  }

  public openDeployContent(content): void {
    this.openDrawer()
    this.getContent(content)
  }

  public openChangeLog(): void {
    this.openDrawer()
    this.latestHuntPackageChangeLog =
      this.deployItem.huntPackage.change_log[
        this.deployItem.huntPackage.change_log.length - 1
      ]
    this.latestContentChangeLog = this.deployItem.huntPackage.content.records
      .filter((content) => {
        return (
          content.change_log[content.change_log.length - 1].note !== 'Initial'
        )
      })
      .map((content) => {
        const lastChange = content.change_log[content.change_log.length - 1]
        return {
          tool: content.tool,
          goal: content.goal,
          categories: content.deployment_requirements.categories.join(', '),
          date: lastChange.date,
          note: lastChange.note,
        }
      })
  }

  public close(): void {
    this.drawerExpanded = false
    this.contentDetails = null
    this.generatedContentDetails = null
    this.latestHuntPackageChangeLog = null
    this.lastFocusedElement?.focus()
    this.lastFocusedElement = null
  }

  private openDrawer(): void {
    this.lastFocusedElement = document.activeElement as HTMLElement
    this.drawerExpanded = true
    setTimeout(() => this.focusDrawerContent())
  }

  private focusDrawerContent(): void {
    const container = this.drawerContent?.nativeElement
    const firstFocusable = container?.querySelector<HTMLElement>(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    ;(firstFocusable ?? container)?.focus()
  }

  // Kendo's drawer (unlike Material's mat-drawer, which manages a focus
  // trap automatically) doesn't move focus into itself on open, trap it
  // while expanded, or restore it on close - and a (keydown.escape) binding
  // on <kendo-drawer> itself never fires since focus never moves there.
  // Handle all three at the document level instead, matching the
  // reliability of the click-outside-to-close behavior.
  @HostListener('document:keydown', ['$event'])
  public onDrawerKeydown(event: KeyboardEvent): void {
    if (!this.drawerExpanded) {
      return
    }

    if (event.key === 'Escape') {
      this.close()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const container = this.drawerContent?.nativeElement
    const focusable = container
      ? Array.from(
          container.querySelectorAll<HTMLElement>(
            'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('disabled'))
      : []

    if (!focusable.length) {
      event.preventDefault()
      container?.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  public markAsDeployed(huntPackageUUID: string): void {
    this.snack.show('Hunt Package Marked Deployed', 5000)
    this.companyService.markAsDeployed(huntPackageUUID).subscribe(() => {
      this.onMarkForDeployedEvent.emit()
    })
  }

  public removeDeployed(huntPackageUUID: string): void {
    this.snack.show('Hunt Package Removed', 5000)
    this.companyService.removeDeployed(huntPackageUUID).subscribe(() => {
      this.onMarkForDeployedEvent.emit()
    })
  }
}
